import { Field, LinkField, Text, useSitecoreContext, ImageField, Image } from '@sitecore-jss/sitecore-jss-nextjs';
import Link from '../../helpers/Link/Link';
import classNames from 'classnames';
import styles from './Footer.module.scss';
import { ComponentProps } from 'lib/component-props';

import { ProcessedItem } from 'src/models/item';
import { IconVariant } from 'components/helpers/Icon';
import { GetLinkFieldIcon } from 'models/fields';
import { useDictionary } from 'lib/utils';

const cx = classNames.bind(styles);

// top-level datasource
type FooterProps = ComponentProps & {
  fields: {
    copyrightLabel: Field<string>;
    bottomRightLink: LinkField;
    sections: FooterSection[];
    materialId?: string;
    lastUpdatedDate?: string;
  };
};

type FooterSection = {
  title: Field<string>;
  columns: GenericLinkItem[][];
};

// a single item underneath a column item that can be of different types (have different fields)
type GenericLinkItem = ProcessedItem & {
  fields: AllPossibleFields;
  columnIndex?: number;
};

type LinkWithLangFields = {
  linkText: Field<string>;
  link: LinkField;
  langAttributeValue: Field<string>;
};

type FooterSocialFields = {
  title: Field<string>;
  facebookLink: LinkField;
  twitterLink: LinkField;
  youtubeLink: LinkField;
  instagramLink: LinkField;
  pinterestLink: LinkField;
};

type FooterAppFields = {
  title: Field<string>;
  appStoreLink: LinkField;
  appStoreImage: ImageField;
  googlePlayLink: LinkField;
  googlePlayImage: ImageField;
};

type AllPossibleFields = LinkWithLangFields | FooterSocialFields | FooterAppFields;

const Footer = (props: FooterProps): React.JSX.Element => {
  const { fields } = props;
  const { sitecoreContext } = useSitecoreContext();
  const $lastUpdatedDate = useDictionary('Last updated');

  const GetFormattedDate = (dateString: string) => {
    if (!dateString) {
      return '';
    }

    if (dateString.length === 8) {
      dateString = `${dateString}T040000Z`;
    }

    const $isoDateString = dateString.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, '$1-$2-$3T$4:$5:$6Z');
    const $date = new Date($isoDateString);

    // Format as MM/DD/YYYY
    const $formattedDate = $date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

    return $formattedDate;
  };

  const SocialLink = (props: { link: LinkField; icon: IconVariant }): React.JSX.Element | null => {
    if (!props.link.value?.href || !props.icon) {
      return null;
    }

    return <Link field={props.link} showLinkTextWithChildrenPresent={false} icon={props.icon} iconAriaHidden={true} />;
  };

  const FooterLink = (prop: {
    link: LinkField;
    lang: Field<string>;
    linkText: Field<string>;
  }): React.JSX.Element | null => {
    if (!prop.link.value?.href && prop.linkText.value == '') {
      return null;
    }

    if (prop.linkText.value != '') {
      return (
        <Link
          field={prop.link}
          className={cx('small')}
          lang={prop.lang?.value}
          showLinkTextWithChildrenPresent={false}
          icon={GetLinkFieldIcon(prop.link)}
          iconAriaHidden={true}
        >
          <Text field={prop.linkText} encode={sitecoreContext.pageEditing} />
        </Link>
      );
    }

    return (
      <Link
        field={prop.link}
        className={cx('small')}
        lang={prop.lang?.value}
        showLinkTextWithChildrenPresent={false}
        icon={GetLinkFieldIcon(prop.link)}
        iconAriaHidden={true}
      >
        {prop.link.value?.text}
      </Link>
    );
  };

  function hasSocialFields(fields: AllPossibleFields): fields is FooterSocialFields {
    return (fields as FooterSocialFields).facebookLink !== undefined;
  }

  function hasAppFields(fields: AllPossibleFields): fields is FooterAppFields {
    return (fields as FooterAppFields).appStoreLink !== undefined;
  }

  let $cmsComplianceInfo = '';

  if (fields.materialId && fields.lastUpdatedDate) {
    const $formattedDate = GetFormattedDate(fields.lastUpdatedDate);
    $cmsComplianceInfo = `${fields.materialId} | ${$lastUpdatedDate}: ${$formattedDate}`;
  }

  return (
    <>
      {$cmsComplianceInfo && (
        <div className={styles.pageInfo}>
          <div className="container-md">{$cmsComplianceInfo}</div>
        </div>
      )}

      <footer className={styles.footer}>
        <div className={cx('container-md', styles.footerMobileContainer)}>
          {fields.sections.map((section, sectionIndex) => {
            const columnCount = section.columns.length;
            // get column with largest count to use as iterator across all columns
            const largestColumnLinkCount = Math.max(...section.columns.map((col) => col.length));
            const totalLinkCountForSection = section.columns.reduce((prev, curr) => {
              // refactor to not count empty links.
              return prev + curr.length;
            }, 0);

            const links: GenericLinkItem[] = [];
            const linkNums: number[][] = [
              [0, 0],
              [0, 0],
            ];

            for (let totalIndex = 0; totalIndex < largestColumnLinkCount; totalIndex++) {
              for (let n = 0; n < columnCount; n++) {
                const currentLink = section.columns[n][totalIndex];
                if (currentLink == null) continue;

                currentLink.columnIndex = n + 1;
                if (hasSocialFields(currentLink.fields)) {
                  linkNums[0] = [n, totalIndex];
                } else if (hasAppFields(currentLink.fields)) {
                  linkNums[1] = [n, totalIndex];
                } else {
                  links.push(currentLink);
                }
              }
            }

            const socialLink = section.columns[linkNums[0][0]][linkNums[0][1]];
            const appLink = section.columns[linkNums[1][0]][linkNums[1][1]];

            const linkUlClass = classNames(
              styles.linksList,
              styles[`linksList__${columnCount}`],
              'links-list',
              'list-inline'
            );

            const linkUlStyles = {
              '--cols':
                hasSocialFields(socialLink.fields) || hasAppFields(appLink.fields) ? columnCount - 1 : columnCount,
              '--colsCount': totalLinkCountForSection,
            } as React.CSSProperties;

            const linkStylesSet = { '--col': 1 } as React.CSSProperties; //set to first column

            const sectionLinkList = links.map((link, i) => {
              const linkStyles = { '--col': link.columnIndex } as React.CSSProperties;

              if (!link.fields.link || hasSocialFields(link.fields) || hasAppFields(link.fields)) {
                return;
              }

              return (
                <li key={i} data-col={link.columnIndex} style={linkStyles}>
                  <FooterLink
                    link={link.fields.link}
                    lang={link.fields.langAttributeValue}
                    linkText={link.fields.linkText}
                  />
                </li>
              );
            });

            return (
              <section className={styles.footerSection} key={sectionIndex}>
                <Text field={section.title} tag="h2" encode={sitecoreContext.pageEditing} className="small" />

                {hasSocialFields(socialLink.fields) || hasAppFields(appLink.fields) ? (
                  // If section has social and/or app links, creating sub-grid to space and arrange links accordingly to Figma.
                  <div
                    className={
                      hasSocialFields(socialLink.fields) || hasAppFields(appLink.fields)
                        ? linkUlClass.concat(` ${styles['linksList__sub-grid']}`)
                        : linkUlClass
                    }
                    style={linkUlStyles}
                  >
                    <ul className={cx('list-inline')}>{sectionLinkList}</ul>
                    <ul className={cx('list-inline')}>
                      {hasSocialFields(socialLink.fields) && (
                        <li className={styles.socialLinks} style={linkStylesSet}>
                          <Text field={socialLink.fields.title} tag="p" className="small fw-semibold" />
                          <SocialLink link={socialLink.fields.facebookLink} icon="facebook" />
                          <SocialLink link={socialLink.fields.twitterLink} icon="twitter" />
                          <SocialLink link={socialLink.fields.youtubeLink} icon="youtube" />
                          <SocialLink link={socialLink.fields.instagramLink} icon="instagram" />
                          <SocialLink link={socialLink.fields.pinterestLink} icon="pinterest" />
                        </li>
                      )}
                      {hasAppFields(appLink.fields) && (
                        <li className={styles.appLinks} style={linkStylesSet}>
                          <Text field={appLink.fields.title} tag="p" className="small fw-semibold" />
                          <Link field={appLink.fields.appStoreLink} showLinkTextWithChildrenPresent={false}>
                            <Image field={appLink.fields.appStoreImage} height="40" width="120" />
                          </Link>
                          <Link field={appLink.fields.googlePlayLink} showLinkTextWithChildrenPresent={false}>
                            <Image field={appLink.fields.googlePlayImage} height="40" width="135" />
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                ) : (
                  <ul className={linkUlClass} style={linkUlStyles}>
                    {sectionLinkList}
                  </ul>
                )}
              </section>
            );
          })}

          <section className={styles.bottomLinks}>
            {sitecoreContext.pageEditing ? (
              <Text field={fields.copyrightLabel} className="text-uppercase" />
            ) : (
              <span className="small d-block text-uppercase fw-semibold">
                {fields.copyrightLabel.value.replace('{currentYear}', new Date().getFullYear().toString())}
              </span>
            )}

            {fields.bottomRightLink?.value.href?.length && fields.bottomRightLink?.value.text?.length ? (
              <Link field={fields.bottomRightLink} className="text-warning small fw-semibold">
                {fields.bottomRightLink?.value?.text}
              </Link>
            ) : null}
          </section>
        </div>
      </footer>
    </>
  );
};

export default Footer;
